import React, { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { getCurrentUser, isMasterContext } from '../services/authService';

// Kept lazy. App.js used to code-split these directly; resolving them here eagerly
// would quietly pull both dashboards into the main bundle.
const Dashboard = lazy(() => import('./Dashboard'));
const AgencyOperatorOverview = lazy(() => import('./AgencyOperatorOverview'));

/**
 * What `/` resolves to. Role alone stopped being enough once an INITIAL_ADMIN
 * could be in one of two places.
 *
 * This is a component rather than the `useMemo(..., [])` App.js used to hold: the
 * memo was computed once at mount, so switching agencies rendered the previous
 * context's dashboard until a full page load. (selectAgency hard-reloads for the
 * same reason; this removes the dependency on that being true.)
 *
 * An INITIAL_ADMIN with no agency selected goes to the picker — their old landing
 * screen, the agency CRM, now lives at `/agencies`. Inside a tenant they get the
 * read-only operator view: they still act as a full admin there, but they arrive as a
 * visitor in a customer's agency, and the agency admin's own page opens on an edit of the
 * customer's registry row. That page stays one link away, at `/agency-settings`.
 *
 * An AGENCY_ADMIN gets the Dashboard everyone else gets: their own approvals, submissions,
 * signatures and review tasks. They may be the agency's chief, with reports routed to them,
 * or the person who keeps Veriform running, at any rank — and either way their own work is
 * what '/' is for. Administering the agency is a separate page with its own navigation entry,
 * at `/administration` (AgencyAdministration).
 */
function RoleDashboard() {
  const user = getCurrentUser();

  if (user?.role === 'INITIAL_ADMIN' && isMasterContext()) {
    return <Navigate to="/select-agency" replace />;
  }

  const screen = user?.role === 'INITIAL_ADMIN' ? <AgencyOperatorOverview /> : <Dashboard />;

  // Its own boundary so the component stays renderable on its own, independent of
  // whichever layout happens to host it.
  return (
    <Suspense fallback={<div className="route-loading" />}>
      {screen}
    </Suspense>
  );
}

export default RoleDashboard;
